import classnames from 'classnames'
import mime from 'mime'
import React, { type ReactNode, useEffect, useRef, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { UserAvatar } from '../UserAvatar/UserAvatar'
import { createImage } from './cropImage'
import { ImageCropModal } from './ImageCropModal'
import { type ImageShape, type ImageUploadLabels, type UploadProgressCallback } from './models'
import { renameFileToMimeType } from './utils'
import styles from './ImageUpload.module.scss'

type ImageUploadProps = {
  imageUrl: string | null
  imageAlt?: string
  shape: ImageShape
  placeholder: ReactNode
  acceptedTypes: string[]
  maxSize: number
  minHeight?: number
  maxDimension?: number
  labels: ImageUploadLabels
  disabled?: boolean
  loading?: boolean
  onUpload: (file: File, onProgress: UploadProgressCallback) => Promise<void>
  onRemove?: () => Promise<void>
  onUploadResult?: (isSuccess: boolean) => void
}

type UploadError = { title: string; description: string }

type LocalPreview = { url: string; baseImageUrl: string | null }

const reconcileFileName = (blob: Blob, fileName: string) => {
  return blob.type !== mime.getType(fileName) ? renameFileToMimeType(fileName, blob.type) : fileName
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrl,
  imageAlt = '',
  shape,
  placeholder,
  acceptedTypes,
  maxSize,
  minHeight,
  maxDimension,
  labels,
  disabled = false,
  loading = false,
  onUpload,
  onRemove,
  onUploadResult,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [preview, setPreview] = useState<LocalPreview | null>(null)
  const [error, setError] = useState<UploadError | null>(null)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropFileName, setCropFileName] = useState('')
  const [cropSourceType, setCropSourceType] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  if (preview && imageUrl !== null && imageUrl !== preview.baseImageUrl) {
    setPreview(null)
  }

  const displayedUrl = preview && (imageUrl === null || preview.baseImageUrl === imageUrl) ? preview.url : imageUrl

  useEffect(() => (preview ? () => URL.revokeObjectURL(preview.url) : undefined), [preview])

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const fail = (uploadError: UploadError) => {
    setError(uploadError)
    onUploadResult?.(false)
    resetInput()
  }

  const triggerBrowse = () => {
    setError(null)
    resetInput()
    fileInputRef.current?.click()
  }

  const openCrop = (src: string, fileName: string, sourceType: string | null) => {
    setCropFileName(fileName)
    setCropSourceType(sourceType)
    setCropSrc(src)
  }

  const processFile = async (file?: File) => {
    if (!file || disabled) {
      return
    }
    if (!acceptedTypes.includes(file.type)) {
      fail({ title: 'Wrong format', description: labels.wrongFormat })
      return
    }
    if (file.size > maxSize) {
      fail({ title: 'File too large', description: labels.tooLarge })
      return
    }
    setError(null)
    const src = URL.createObjectURL(file)
    if (minHeight) {
      try {
        const image = await createImage(src)
        if (image.naturalHeight < minHeight) {
          URL.revokeObjectURL(src)
          fail({
            title: 'Image too short',
            description: `Minimum height: ${minHeight} px. Uploaded: ${image.naturalWidth}x${image.naturalHeight} px.`,
          })
          return
        }
      } catch {
        URL.revokeObjectURL(src)
        fail({ title: 'Could not read image', description: 'Please try another file.' })
        return
      }
    }
    openCrop(src, file.name, file.type)
  }

  const closeCropModal = () => {
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc)
    }
    setCropSrc(null)
    resetInput()
  }

  const handleCropConfirm = async (blob: Blob) => {
    if (blob.size > maxSize) {
      closeCropModal()
      fail({ title: 'File too large', description: labels.tooLarge })
      return
    }
    const fileName = reconcileFileName(blob, cropFileName)
    closeCropModal()
    setCropFileName(fileName)
    setProgress(0)
    setIsUploading(true)
    try {
      await onUpload(new File([blob], fileName, { type: blob.type }), setProgress)
      setProgress(100)
      setPreview({ url: URL.createObjectURL(blob), baseImageUrl: imageUrl })
      onUploadResult?.(true)
    } catch {
      setError({ title: 'Upload failed', description: 'Please try again.' })
      onUploadResult?.(false)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async () => {
    if (!onRemove) {
      return
    }
    setError(null)
    setIsRemoving(true)
    try {
      await onRemove()
      setPreview(null)
    } catch {
      setError({ title: labels.removeFailed, description: 'Please try again.' })
    } finally {
      setIsRemoving(false)
    }
  }

  const renderPreview = () => {
    if (displayedUrl && shape === 'circle') {
      return <UserAvatar width='64px' height='64px' imageUrl={displayedUrl} />
    }
    if (displayedUrl) {
      return <img className={styles.rectPreview} src={displayedUrl} alt={imageAlt} />
    }
    if (loading) {
      return (
        <div
          aria-hidden='true'
          data-testid='image-upload-skeleton'
          className={classnames('skeleton flex-shrink-0', shape === 'circle' ? classnames('rounded-circle', styles.placeholderCircle) : styles.rectSkeleton)}
        />
      )
    }
    if (shape === 'circle') {
      return (
        <div
          className={classnames(
            'd-flex align-items-center justify-content-center flex-shrink-0 rounded-circle zocks-bg-color-gray-200',
            styles.placeholderCircle,
          )}
        >
          <span className='fw-bold zocks-text-gray-900'>{placeholder}</span>
        </div>
      )
    }
    return <div className={classnames('flex-shrink-0', styles.placeholderRect)}>{placeholder}</div>
  }

  const renderSide = () => {
    if (isUploading) {
      return (
        <div
          className={classnames('d-flex flex-column align-items-center gap-2 py-3 px-3 rounded-3 zocks-border-gray-600 zocks-border-dashed', styles.dropzone)}
        >
          <div className='d-flex flex-column align-items-center'>
            <i className='bi bi-arrow-repeat' />
            <span className='paragraph-small zocks-text-gray-900'>Uploading {cropFileName}...</span>
          </div>
          <span className='paragraph-small zocks-text-primary'>{progress}%</span>
          <ProgressBar now={progress} className={styles.progress} />
        </div>
      )
    }
    if (isRemoving) {
      return <div className={classnames('paragraph-medium', styles.actions)}>{labels.removing}</div>
    }
    if (loading && !displayedUrl) {
      return <div className={classnames('paragraph-medium', styles.actions)}>Loading...</div>
    }
    if (displayedUrl) {
      return (
        <div className={classnames('d-flex flex-column zocks-gap-0.75', styles.actions)}>
          <Button variant='header' className='text-nowrap' disabled={disabled} onClick={triggerBrowse}>
            {labels.change}
          </Button>
          {onRemove ? (
            <button
              type='button'
              disabled={disabled}
              onClick={handleRemove}
              className='btn btn-link p-0 d-flex align-items-center gap-1 paragraph-medium zocks-text-gray-900 text-decoration-none'
            >
              <i className='bi bi-trash3' />
              {labels.remove}
            </button>
          ) : null}
        </div>
      )
    }
    return (
      <div
        className={classnames(
          'd-flex flex-column align-items-center gap-2 py-3 px-3 rounded-3 zocks-bg-color-gray-100 zocks-border-gray-600 zocks-border-dashed',
          styles.dropzone,
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          processFile(e.dataTransfer.files?.[0])
        }}
      >
        <i className='bi bi-upload fs-6 lh-1' />
        <div className='d-flex flex-column align-items-center zocks-gap-0.125 text-center'>
          <span className='paragraph-medium fw-bold zocks-text-gray-900'>{labels.drop}</span>
          <span className='paragraph-small zocks-text-gray-700'>{labels.hint}</span>
        </div>
        <button
          type='button'
          disabled={disabled}
          onClick={triggerBrowse}
          className='btn btn-link p-0 border-0 paragraph-medium zocks-text-primary text-decoration-underline'
        >
          Browse files
        </button>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <input ref={fileInputRef} type='file' accept={acceptedTypes.join(',')} onChange={(e) => processFile(e.target.files?.[0])} hidden />

      <div
        className={classnames('d-inline-flex align-items-center align-self-start gap-4 p-4 rounded-3 zocks-border-gray-500', {
          [styles.emptyRectCard]: shape === 'rectangle' && !displayedUrl,
        })}
      >
        {renderPreview()}
        {renderSide()}
      </div>

      {error ? (
        <div className='d-flex align-items-start gap-1'>
          <i className='bi bi-exclamation-circle zocks-text-danger-200' />
          <div>
            <div className='paragraph-small fw-bold zocks-text-danger-200'>{error.title}</div>
            <div className='paragraph-small zocks-text-danger-200'>{error.description}</div>
          </div>
        </div>
      ) : null}

      {cropSrc ? (
        <ImageCropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          sourceType={cropSourceType}
          shape={shape}
          title={labels.cropTitle}
          saveTitle={labels.cropSave}
          minHeight={minHeight}
          maxDimension={maxDimension}
          onCancel={closeCropModal}
          onConfirm={handleCropConfirm}
        />
      ) : null}
    </div>
  )
}
